import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { ApiError } from '../../../services/api';
import { getBillingOrganizationSummary } from '../../../services/admin-billing';
import type { BillingOrganizationSummary } from '../../../types/adminBilling';
import { BillingContactsPanel } from './BillingContactsPanel';
import { BillingInvoicesPanel } from './BillingInvoicesPanel';
import { BillingPlanPanel } from './BillingPlanPanel';
import { BillingSetupGuide } from './BillingSetupGuide';
import { isBillingSetupComplete } from './billingSetupState';
import { BillingSetupWizard } from './BillingSetupWizard';
import { BillingStripePanel } from './BillingStripePanel';
import { BillingSummaryPanel } from './BillingSummaryPanel';
import { PauseOrganizationModal } from '../PauseOrganizationModal';
import { ResumeOrganizationModal } from '../ResumeOrganizationModal';
import { useOrganizationPause } from '../useOrganizationPause';
import { AdminPage, AdminPageHeader } from '../AdminPage';
import './Billing.css';

/**
 * One organization's billing: what it is on, who is told about it, and every invoice.
 *
 * The page owns the **summary** and nothing else. Each panel below loads the resource it edits,
 * because they are edited independently and a single page-level fetch would mean every save
 * reloading all four. What the page does own is the consequence: a plan save changes the next
 * invoice date, a contact change changes who can be reached, and marking an invoice paid changes
 * the outstanding total, so every panel is handed `refreshSummary` and the block at the top always
 * agrees with the panels under it.
 */
export const BillingOrganizationDetail = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const [summary, setSummary] = useState<BillingOrganizationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useDocumentTitle(summary ? `${summary.organizationName} billing` : null);

  useEffect(() => {
    if (!orgId) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    getBillingOrganizationSummary(orgId, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setSummary(loaded);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : 'Could not load the billing for this organization.'
        );
        setLoading(false);
      });

    return () => controller.abort();
  }, [orgId, reloadToken]);

  const refreshSummary = useCallback(() => setReloadToken((token) => token + 1), []);

  /*
   * Pausing is the answer to an overdue account that has stopped being a conversation, and this is
   * the screen where an admin has just read the invoices that led them there. It is the SAME
   * suspension as the one on the organization screen, through the same hook and the same two
   * dialogs, rather than a second idea of disabling an organization living in billing.
   *
   * Nothing automatic ever reaches it. No sweep, generator or webhook pauses anybody; the whole
   * point of putting the button here is that a person decides, having looked.
   */
  const pause = useOrganizationPause({
    organizationId: orgId,
    onPauseStateChanged: (result) => {
      // Written straight onto the summary as well as refetched, so the header stops offering Pause
      // the moment it succeeds rather than a request later.
      setSummary((current) => (current ? { ...current, isPaused: result.isPaused } : current));
      refreshSummary();
    },
  });

  if (!orgId) {
    return (
      <AdminPage>
        <p className="admin-state">No organization was named in the address.</p>
        <div>
          <Link className="billing-detail__back" to="/admin/billing">
            Back to billing
          </Link>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage labelledBy="billing-detail-title">
      <AdminPageHeader
        title={summary ? summary.organizationName : 'Organization billing'}
        titleId="billing-detail-title"
        subtitle="The plan, the accounts payable contacts and every invoice for this organization."
        back={
          <Link className="billing-detail__back" to="/admin/billing">
            Back to billing
          </Link>
        }
        /*
          Only once the summary has loaded: which of Pause and Resume this is depends on the
          answer, and a button that changes its own meaning a moment after it appears is one an
          admin can press meaning the other.
        */
        actions={
          summary && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => (summary.isPaused ? pause.openUnpause() : pause.openPause(null))}
              data-testid="billing-pause-toggle"
            >
              {summary.isPaused ? 'Resume organization' : 'Pause organization'}
            </Button>
          )
        }
      />

      {summary?.isPaused && (
        <p className="billing__banner billing__banner--warning" data-testid="billing-detail-paused">
          This organization is paused, so nobody in it can use RosterPilot. Resume it once the bill
          is settled.
        </p>
      )}

      {error && (
        <div className="admin-error" role="alert">
          <span>{error}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={refreshSummary}
            data-testid="billing-detail-retry"
          >
            Try again
          </Button>
        </div>
      )}

      {loading && !summary ? (
        <p className="admin-state">Loading billing...</p>
      ) : (
        <>
          {/*
            The wizard while there is still setting up to do, the guide once there is not.

            Both answer the same question, so only one earns the space: the wizard walks the four
            steps in order and writes at each one, and the guide is the standing summary for an
            organization already billed. Showing both would put two lists of the same steps above
            the panels that do them.
          */}
          {isBillingSetupComplete(summary) ? (
            <BillingSetupGuide summary={summary} />
          ) : (
            <BillingSetupWizard
              organizationId={orgId}
              organizationName={summary?.organizationName ?? 'this organization'}
              summary={summary}
              onChanged={refreshSummary}
            />
          )}

          {summary && <BillingSummaryPanel summary={summary} />}

          <BillingPlanPanel organizationId={orgId} onSaved={refreshSummary} />

          <BillingContactsPanel organizationId={orgId} onChanged={refreshSummary} />

          {/*
            Stripe is per organization: its customer, its saved method, its invoices. The reminder
            email copy is not - the same three emails go to every organization - so that lives on
            its own screen beside the billing list rather than here.
          */}
          <BillingStripePanel
            organizationId={orgId}
            organizationName={summary?.organizationName ?? 'this organization'}
            isStripeManaged={summary?.isStripeManaged ?? false}
            /*
              The plan is the precondition for everything Stripe, and the source of every default a
              subscription falls back to. Undefined while the summary loads is passed as null: the
              panel only ever uses it to take controls away and to prefill, so "no plan yet" is the
              safe reading of "not known yet".
            */
            plan={summary?.plan ?? null}
            onChanged={refreshSummary}
          />

          <BillingInvoicesPanel
            organizationId={orgId}
            organizationName={summary?.organizationName ?? 'this organization'}
            defaultCurrency={summary?.plan?.currency ?? 'USD'}
            /*
              Defaults to false while the summary is still loading, which is the safe direction:
              the panel only ever uses it to take controls away, and a row's own
              `canSettleLocally` is what gates the writes regardless.
            */
            isStripeManaged={summary?.isStripeManaged ?? false}
            onChanged={refreshSummary}
          />
        </>
      )}

      <PauseOrganizationModal
        isOpen={pause.modal === 'pause'}
        organizationName={summary?.organizationName ?? 'this organization'}
        message={pause.message}
        onMessageChange={pause.setMessage}
        loading={pause.loading}
        error={pause.error}
        onConfirm={pause.confirmPause}
        onClose={pause.close}
      />

      <ResumeOrganizationModal
        isOpen={pause.modal === 'unpause'}
        organizationName={summary?.organizationName ?? 'this organization'}
        loading={pause.loading}
        error={pause.error}
        onConfirm={pause.confirmUnpause}
        onClose={pause.close}
      />
    </AdminPage>
  );
};
